import { useRef, useState } from 'react'

import { Box, useClassNames } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index.styles.css'
import { Resize } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Header/Resize'
import { ColumnsType } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import {
  COVERAGE_SECTION_HEIGHT,
  COVERAGE_SECTION_MIN_HEIGHT,
  HEADER_OFFSET,
} from 'modules/shift_management/lib/consts/ShiftManagementCoverage'
import type { Shift } from 'modules/shift_management/lib/types'

import { stylesRefs } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index.styles'
import CoverageSummary from './CoverageSummary'
import CoverageTitle from './CoverageTitle'

interface Props {
  columns: ColumnsType
  coverageShifts: Shift[]
  hover: boolean
  setHover: (hover: boolean) => void
  resizeRef: React.RefObject<HTMLDivElement>
}

const Header = ({ columns, coverageShifts, hover, setHover, resizeRef }: Props) => {
  const [headerHover, setHeaderHover] = useState(false)
  const [, setLocalStorageResizeHeight] = useLocalStorage<number>(
    LOCAL_STORAGE_KEYS.shiftManagementCoverageResizeHeight,
    0
  )
  const isResizingRef = useRef(false)
  const isTransitioningRef = useRef(false)
  const cx = useClassNames(stylesRefs)
  const [isOpen, setIsOpen] = useState(false)

  useUnsafeEffect(() => {
    if (!resizeRef.current) return

    resizeRef.current.offsetHeight > HEADER_OFFSET ? setIsOpen(true) : setIsOpen(false)
  }, [resizeRef, resizeRef.current?.offsetHeight])

  const handleClick = () => {
    if (!resizeRef.current || isResizingRef.current) return

    // Disable resize during the collapse/expand animation
    isTransitioningRef.current = true

    isOpen
      ? track(trackingEvents.shiftManagement.collapseCoverageSection)
      : track(trackingEvents.shiftManagement.expandCoverageSection)
    isOpen
      ? setLocalStorageResizeHeight(COVERAGE_SECTION_MIN_HEIGHT)
      : setLocalStorageResizeHeight(COVERAGE_SECTION_HEIGHT)

    // Re-enable resize after transition completes
    setTimeout(() => {
      isTransitioningRef.current = false
    }, 300)
  }

  return (
    <Box
      onMouseEnter={() => setHeaderHover(true)}
      onMouseLeave={() => setHeaderHover(false)}
      width="full"
      height="s32"
      border={{
        top: { width: 's1', style: 'solid', color: 'grey300' },
        bottom: { width: 's1', style: 'solid', color: 'grey300' },
        right: { width: 's1', style: 'solid', color: 'grey300' },
      }}
      className={`${cx('header', 'sticky')} ${
        headerHover ? 'bg-f1-background-hover' : 'bg-f1-background'
      }`}
      style={{
        boxShadow: hover ? '0 -3px 0 0 rgba(1, 27, 75, 0.37)' : undefined,
        boxSizing: 'content-box',
        cursor: hover && !isTransitioningRef.current ? 'row-resize' : 'default',
      }}
    >
      <Resize
        resizeRef={resizeRef}
        setHover={setHover}
        setHeaderHover={setHeaderHover}
        isResizing={isResizingRef}
        isTransitioning={isTransitioningRef}
      />
      <Box flexDirection="row" className={cx('gridHeader')} height="full" onClick={handleClick}>
        <CoverageTitle isOpen={isOpen} />
        <CoverageSummary columns={columns} coverageShifts={coverageShifts} />
      </Box>
    </Box>
  )
}

export default Header
